import { Card, Typography, Chip, Stack, Button, Collapse } from "@mui/material";
import { Box } from "@mui/system";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../../assets/styles/tokens";
import IncomeGapsMoreMetrics from "./IncomeGapsMoreMetrics";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React from "react";
import {
  PERCENT_STYLES,
  cleanNumber,
  formatUsd,
} from "../../../../../../../DashboardDesktop/Anomalies/AnomaliesTabs/Utils/anomaliesColumns";

// ─────────────────────────────────────────────────────────────────────────────

// Gap indicator (same convention as the existing Business Development
// screens, e.g. desktop IncomeGapsTable's isHighlighted): red when the gap
// magnitude reaches the threshold, default styling otherwise.
const GAP_HIGHLIGHT_THRESHOLD = 500;

interface IncomeGapsCardProps {
  row: Record<string, any>;
}

const GapChip = ({ label, value }: { label: string; value: any }) => {
  const num = cleanNumber(value);
  const isHighlighted = Math.abs(num) >= GAP_HIGHLIGHT_THRESHOLD;
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Chip
        label={formatUsd(value)}
        size="medium"
        sx={{
          borderRadius: spacing.Radius.xs.$value,
          fontSize: typography.Size.Text.md.$value,
          bgcolor: isHighlighted
            ? PERCENT_STYLES.red.bg
            : colors.GrayLightMode[100].$value,
          color: isHighlighted
            ? PERCENT_STYLES.red.color
            : colors.GrayLightMode[900].$value,
          fontWeight: typography.Font_weight.Semibold.$value,
        }}
      />
      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[900].$value,
          mx: spacing.Spacing.xs.$value,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export function IncomeGapsCard({ row }: IncomeGapsCardProps) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Card
        sx={{
          p: 1,
          borderRadius: spacing.Radius.lg.$value,
          boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
          border: "1px solid" + colors.GrayLightMode[200].$value,
          mb: spacing.Spacing.none.$value,
          mt: spacing.Spacing.xl.$value + "!important",
        }}
      >
        {/* Top Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body1"
            sx={{
              color: colors.GrayLightMode[700].$value,
              fontSize: typography.Size.Text.xs.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              mb: spacing.Spacing.md.$value,
            }}
          >
            {row.brand_display_name} ({row.brand_id})
          </Typography>
        </Stack>

        <GapChip label="CPA Gap" value={row.cpa_gap} />

        <Stack
          direction="row"
          justifyContent="space-between"
          mt={spacing.Spacing.xs.$value}
          mb={spacing.Spacing.md.$value}
        >
          <Box
            sx={{
              width: "50%",
              borderRight: "1px solid" + colors.GrayLightMode[200].$value,
              pr: spacing.Spacing.md.$value,
            }}
          >
            <Typography
              mb={spacing.Spacing.xs.$value}
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontSize: typography.Size.Text.xs.$value,
              }}
            >
              CPA Income
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: colors.GrayLightMode[900].$value,
                  fontSize: typography.Size.Text.md.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  pr: spacing.Spacing.xs.$value,
                }}
              >
                {formatUsd(row.org_cpa_income)}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: "50%",
              pl: spacing.Spacing.md.$value,
            }}
          >
            <Typography
              mb={spacing.Spacing.xs.$value}
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontSize: typography.Size.Text.xs.$value,
              }}
            >
              Calculated CPA Income
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: colors.GrayLightMode[900].$value,
                  fontSize: typography.Size.Text.md.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  pr: spacing.Spacing.xs.$value,
                }}
              >
                {formatUsd(row.cul_cpa_income)}
              </Typography>
            </Box>
          </Box>
        </Stack>

        <GapChip label="RS Gap" value={row.rs_gap} />

        <Stack
          direction="row"
          justifyContent="space-between"
          mt={spacing.Spacing.xs.$value}
          mb={spacing.Spacing.md.$value}
        >
          <Box
            sx={{
              width: "50%",
              borderRight: "1px solid" + colors.GrayLightMode[200].$value,
              pr: spacing.Spacing.md.$value,
            }}
          >
            <Typography
              mb={spacing.Spacing.xs.$value}
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontSize: typography.Size.Text.xs.$value,
              }}
            >
              RS Income
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: colors.GrayLightMode[900].$value,
                  fontSize: typography.Size.Text.md.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  pr: spacing.Spacing.xs.$value,
                }}
              >
                {formatUsd(row.org_rs_income)}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: "50%",
              pl: spacing.Spacing.md.$value,
            }}
          >
            <Typography
              mb={spacing.Spacing.xs.$value}
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontSize: typography.Size.Text.xs.$value,
              }}
            >
              Calculated RS Income
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: colors.GrayLightMode[900].$value,
                  fontSize: typography.Size.Text.md.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  pr: spacing.Spacing.xs.$value,
                }}
              >
                {formatUsd(row.cul_rs_income)}
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="center">
          <Button
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              padding: "0",
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              color: colors.Brand[800].$value,
              fontWeight: typography.Font_weight.Regular.$value,
              textTransform: "capitalize",
              letterSpacing: "0px",
            }}
          >
            {open ? "Show Less Metrics" : "Show More Metrics"}
            <ExpandMoreIcon
              sx={{
                color: colors.Brand[800].$value,
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.2s",
              }}
            />
          </Button>
        </Stack>
        <Stack>
          {/* Expandable Info */}
          <Collapse in={open}>
            <IncomeGapsMoreMetrics row={row} />
          </Collapse>
        </Stack>
      </Card>
    </>
  );
}
export default IncomeGapsCard;
